import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { invitationsAPI } from '../api/api';
import { CheckCircle2, XCircle, Loader2, LogIn, UserPlus } from 'lucide-react';
import SkillDeckLogo from '../components/SkillDeckLogo';

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [status, setStatus] = useState('idle'); // idle | loading | success | error | no-token
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const acceptInvite = useCallback(async () => {
    if (!token) {
      setStatus('no-token');
      return;
    }
    setStatus('loading');
    try {
      const data = await invitationsAPI.accept(token);
      setResult(data);
      setStatus('success');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Impossible d\'accepter l\'invitation';
      setErrorMessage(detail);
      setStatus('error');
    }
  }, [token]);

  useEffect(() => {
    if (authLoading) return;

    if (!token) {
      setStatus('no-token');
      return;
    }

    if (user) {
      // User is logged in — accept immediately
      acceptInvite();
    } else {
      // Not logged in — store token for after auth
      sessionStorage.setItem('pendingInviteToken', token);
      setStatus('idle');
    }
  }, [user, authLoading, token, acceptInvite]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#05050f] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05050f] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3">
            <SkillDeckLogo size={40} />
            <span className="text-xl font-bold text-white">Skilldeck</span>
          </div>
        </div>

        <div className="bg-[#0d0d24] border border-[rgba(108,71,255,.3)] rounded-2xl shadow-2xl p-8">
          {/* No token */}
          {status === 'no-token' && (
            <div className="text-center">
              <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Lien invalide</h2>
              <p className="text-gray-400 text-sm mb-6">Ce lien d'invitation est incomplet ou invalide.</p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-medium transition-colors text-sm"
              >
                Retour à l'accueil
              </button>
            </div>
          )}

          {/* Loading */}
          {status === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="h-10 w-10 text-violet-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-300">Acceptation de l'invitation...</p>
            </div>
          )}

          {/* Success */}
          {status === 'success' && result && (
            <div className="text-center">
              <div className="h-14 w-14 rounded-full bg-emerald-900/40 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">
                {result.message === 'Already a member' ? 'Vous êtes déjà membre !' : 'Bienvenue !'}
              </h2>
              <p className="text-gray-400 text-sm mb-1">
                Vous avez rejoint <strong className="text-white">{result.organization_name}</strong>
              </p>
              <p className="text-gray-500 text-xs mb-6">
                Rôle : <span className="text-violet-400 font-medium">{result.role}</span>
              </p>
              <button
                onClick={() => {
                  // Switch to the new org
                  localStorage.setItem('currentOrgId', result.organization_id);
                  navigate('/dashboard');
                  window.location.reload();
                }}
                className="px-6 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-medium transition-colors text-sm"
              >
                Accéder au tableau de bord
              </button>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="text-center">
              <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Invitation échouée</h2>
              <p className="text-gray-400 text-sm mb-6">{errorMessage}</p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-medium transition-colors text-sm"
              >
                Retour à l'accueil
              </button>
            </div>
          )}

          {/* Not logged in */}
          {status === 'idle' && !user && (
            <div className="text-center">
              <UserPlus className="h-12 w-12 text-violet-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Vous êtes invité(e) !</h2>
              <p className="text-gray-400 text-sm mb-6">
                Connectez-vous ou créez un compte pour accepter l'invitation.
              </p>
              <div className="space-y-3">
                <Link
                  to={`/login`}
                  className="block w-full px-4 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-medium transition-colors text-sm text-center"
                >
                  <LogIn className="h-4 w-4 inline mr-2" />
                  Se connecter
                </Link>
                <Link
                  to="/register"
                  className="block w-full px-4 py-2.5 border border-violet-600 text-violet-400 hover:bg-violet-600/10 rounded-lg font-medium transition-colors text-sm text-center"
                >
                  Créer un compte
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
